import type { FlightState, SwingParams, Tempo, Transition, Vec3 } from '../types';
import { MPH_TO_MS, DEG_TO_RAD, RAD_TO_DEG } from '../constants';
import { normalizeVec3 } from '../physics/vec3Math';

/**
 * Derive face-to-path angle from face angle and club path.
 * Positive = face open to path (fade/slice)
 * Negative = face closed to path (draw/hook)
 */
export function computeFaceToPath(faceAngle: number, clubPath: number): number {
  return faceAngle - clubPath;
}

/**
 * Estimate spin loft from launch conditions.
 * Spin loft = dynamic loft − attack angle.
 * Dynamic loft ≈ launchAngle / 0.80 (launch is ~80% of dynamic loft on average).
 *
 * Results across clubs:
 *   Driver:  dynLoft ≈ 13.1°, spinLoft ≈ 10.1°
 *   7-Iron:  dynLoft ≈ 20.6°, spinLoft ≈ 24.9°
 *   SW:      dynLoft ≈ 40.0°, spinLoft ≈ 46.0°
 */
export function estimateSpinLoft(launchAngle: number, attackAngle: number): number {
  const dynamicLoft = launchAngle / 0.80;
  return Math.max(5, dynamicLoft - attackAngle); // floor at 5° to avoid division instability
}

/**
 * Derive spin axis from D-plane model with loft-dependent scaling.
 *
 * The spin axis tilt = atan2(sin(faceToPath), sin(spinLoft)).
 * With a driver (low spinLoft ~10°), the same face-to-path produces
 * dramatically more spin axis tilt than a wedge (high spinLoft ~46°).
 *
 * This is why a 5° open face with a driver creates a huge slice,
 * but 5° open with a sand wedge barely curves at all.
 */
export function deriveSpinAxisFromImpact(
  faceAngle: number,
  clubPath: number,
  launchAngle: number,
  attackAngle: number,
): number {
  const faceToPath = computeFaceToPath(faceAngle, clubPath);
  if (Math.abs(faceToPath) < 0.01) return 0; // dead straight

  const spinLoft = estimateSpinLoft(launchAngle, attackAngle);
  const ftpRad = faceToPath * DEG_TO_RAD;
  const slRad = spinLoft * DEG_TO_RAD;

  const spinAxis = Math.atan2(Math.sin(ftpRad), Math.sin(slRad)) * RAD_TO_DEG;
  return Math.round(spinAxis * 10) / 10;
}

/**
 * Derive launch direction from face angle, club path, and spin loft.
 *
 * The face-contribution ratio varies by loft:
 *   Driver (~10° spinLoft): ~84% face, ~16% path
 *   7-Iron (~25° spinLoft): ~77% face, ~23% path
 *   SW     (~46° spinLoft): ~68% face, ~32% path
 *
 * Higher loft → less face influence, more path influence on start line.
 */
export function deriveLaunchDirection(
  faceAngle: number,
  clubPath: number,
  launchAngle: number,
  attackAngle: number,
): number {
  const spinLoft = estimateSpinLoft(launchAngle, attackAngle);
  // Face contribution decreases as spin loft increases
  const faceRatio = 0.68 + 0.20 * Math.max(0, 1 - spinLoft / 45);
  return faceAngle * faceRatio + clubPath * (1 - faceRatio);
}

/**
 * Apply club delivery interaction: face angle and attack angle dynamically
 * modify launch conditions beyond the preset baseline.
 *
 * Models three real-world effects:
 *   1. Open face adds dynamic loft → higher launch, more spin
 *   2. Steeper attack adds spin loft → more spin, less ball speed
 *   3. Face-to-path creates spin axis tilt (already in deriveSpinAxisFromImpact)
 *
 * These effects are SMALL per-degree adjustments layered on top of the
 * preset baseline, not full recomputations. The preset already encodes
 * "typical" delivery for each club; this adjusts for individual variance.
 */
export function applyDeliveryInteraction(params: SwingParams): SwingParams {
  // 1) Open/closed face changes effective loft:
  //    Open face adds ~0.6° dynamic loft per degree open
  //    → raises launch by ~0.4° and adds ~80 rpm per degree
  const faceLoftEffect = params.faceAngle * 0.6;
  const launchFromFace = faceLoftEffect * 0.4;
  const spinFromFace = faceLoftEffect * 80;

  // 2) Attack angle affects spin loft:
  //    More negative (steeper) = more spin loft = more backspin
  //    The preset already has a baseline AoA; we adjust for DEVIATION
  //    from that baseline via the variance system
  //    Effect: ~120 rpm per degree steeper than baseline
  // (This is already captured in the preset; only face effects are new)

  // 3) Gear effect from face-to-path: already handled in deriveSpinAxisFromImpact

  // 4) Ball speed loss from open/closed face:
  //    Off-center strike on face costs COR. Open/closed also loses ~0.2% per degree
  const speedPenalty = Math.abs(params.faceAngle) * 0.002;

  return {
    ...params,
    launchAngle: params.launchAngle + launchFromFace,
    spinRate: Math.max(0, params.spinRate + spinFromFace),
    ballSpeed: Math.max(0, params.ballSpeed * (1 - speedPenalty)),
  };
}

/**
 * Convert ball launch conditions to a FlightState. Measured (or already
 * equipment-adjusted) ball data must not have delivery applied a second time.
 * The estimated mode explicitly opts into the legacy delivery heuristic.
 */
export function computeLaunchState(params: SwingParams, mode: 'measured' | 'estimated' = 'measured'): FlightState {
  const adjusted = mode === 'estimated' ? applyDeliveryInteraction(params) : params;

  const ballSpeedMs = adjusted.ballSpeed * MPH_TO_MS;
  const launchAngleRad = adjusted.launchAngle * DEG_TO_RAD;
  const launchDirRad = adjusted.launchDirection * DEG_TO_RAD;

  // Velocity components
  const vz = ballSpeedMs * Math.cos(launchAngleRad) * Math.cos(launchDirRad);
  const vx = ballSpeedMs * Math.cos(launchAngleRad) * Math.sin(launchDirRad);
  const vy = ballSpeedMs * Math.sin(launchAngleRad);

  // Spin axis: tilt from pure backspin by spinAxis degrees
  // Pure backspin axis = (-1, 0, 0) — cross(-1,0,0) x (0,0,1) = (0,1,0) = lift
  // Tilting around the Z-axis (velocity direction) adds Y component:
  //   +tilt → axis gains +Y → cross gives +X force (ball curves right = slice)
  //   -tilt → axis gains -Y → cross gives -X force (ball curves left = hook)
  const spinAxisTiltRad = adjusted.spinAxis * DEG_TO_RAD;

  const spinAxisVec: Vec3 = normalizeVec3({
    x: -Math.cos(spinAxisTiltRad),
    y: Math.sin(spinAxisTiltRad),
    z: 0,
  });

  // Rotate spin axis around Y-axis to account for launch direction
  const cosDir = Math.cos(launchDirRad);
  const sinDir = Math.sin(launchDirRad);
  const rotatedSpinAxis: Vec3 = normalizeVec3({
    x: spinAxisVec.x * cosDir + spinAxisVec.z * sinDir,
    y: spinAxisVec.y,
    z: -spinAxisVec.x * sinDir + spinAxisVec.z * cosDir,
  });

  return {
    // Flight heights are relative to the launch/landing surface; renderer adds radius.
    position: { x: 0, y: 0, z: 0 },
    velocity: { x: vx, y: vy, z: vz },
    spinRate: adjusted.spinRate,
    spinAxis: rotatedSpinAxis,
    time: 0,
  };
}

/**
 * Create SwingParams from the full UI inputs.
 */
export function createSwingParams(
  clubSpeed: number,
  ballSpeed: number,
  launchAngle: number,
  launchDirection: number,
  spinRate: number,
  spinAxis: number,
  attackAngle: number,
  clubPath: number,
  faceAngle: number,
  tempo: Tempo = 'medium',
  transition: Transition = 'medium',
): SwingParams {
  return {
    clubSpeed,
    ballSpeed,
    launchAngle,
    launchDirection,
    spinRate,
    spinAxis,
    attackAngle,
    clubPath,
    faceAngle,
    tempo,
    transition,
  };
}
